import { Link } from "react-router-dom";
import { SECTORS } from "../data/sectors";
import LaurelSeal from './LaurelSeal';
import { CURRENT_SEASON } from "../data/season";

// Glifos propios en vez de un ícono más de lucide-react + una librería nueva
// solo por TikTok (que lucide no trae): dos trazos monocromos al mismo peso
// visual, currentColor, coherentes entre sí — no un ícono de línea mezclado
// con uno de relleno de otra librería.
function FacebookGlyph(props) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
            <path d="M14 13.5h2.5l1-4H14v-2c0-1.03 0-2 2-2h1.5V2.14C17.17 2.1 15.95 2 14.66 2 11.98 2 10 3.66 10 6.7v2.8H7v4h3V22h4v-8.5Z" />
        </svg>
    );
}
function TikTokGlyph(props) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
            <path d="M16.5 2h-3v13.2a2.8 2.8 0 1 1-2.3-2.76V9.36a6 6 0 1 0 5.3 5.96V8.2a7.6 7.6 0 0 0 4.5 1.46V6.6A4.6 4.6 0 0 1 16.5 2Z" />
        </svg>
    );
}

const SOCIAL = [
    { name: 'Facebook', href: 'https://www.facebook.com/carpepel', Icon: FacebookGlyph },
    { name: 'TikTok', href: 'https://www.tiktok.com/@carpepel', Icon: TikTokGlyph },
];

export default function Footer() {
    return (
        <footer className="border-t border-[var(--line)] bg-[var(--paper-2)]" data-testid="footer">
            {/* Saludo de temporada: mismo cordel rojo que baja de la barra. */}
            {CURRENT_SEASON?.id === 'navidad' && (
                <div data-testid="footer-season-greeting">
                    <div
                        aria-hidden="true"
                        className="h-[3px]"
                        style={{ background: 'repeating-linear-gradient(135deg, var(--nochebuena) 0 6px, var(--paper-2) 6px 12px)' }}
                    />
                    <p className="text-center font-mono text-[11px] uppercase tracking-[0.2em] py-3" style={{ color: 'var(--nochebuena)' }}>
                        Felices fiestas · De parte de todo el equipo de Carpepel
                    </p>
                </div>
            )}
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
                {/* Enlaces por giro: además de servir al visitante que llega por
                    otra puerta, es el enlazado interno que impide que las
                    páginas de sector cuelguen de un solo bloque en Home. */}
                <nav
                    aria-label="Papel por tipo de negocio"
                    className="pb-6 mb-6 border-b border-[var(--line)]"
                    data-testid="footer-sectors"
                >
                    <p className="font-mono text-[11px] uppercase tracking-[0.14em] mb-3 text-center md:text-left" style={{ color: 'var(--ink-soft)' }}>
                        Papel por tipo de negocio
                    </p>
                    <ul className="flex flex-wrap justify-center md:justify-start gap-x-5 gap-y-2">
                        {SECTORS.map((s) => (
                            <li key={s.slug}>
                                <Link
                                    to={`/papel-para/${s.slug}`}
                                    data-testid={`footer-sector-${s.slug}`}
                                    className="text-xs text-[var(--ink-soft)] transition-colors duration-200 hover:text-[var(--kraft-deep)]"
                                >
                                    {s.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-5">
                        <img
                            src="/img/carpepel-assets/site_logo_opt.webp"
                            alt="Carpepel"
                            className="h-8 w-auto object-contain"
                            /* Mismo motivo que en Navbar: el archivo del logo
                               lleva fondo blanco opaco y sobre --paper-2 se veía
                               como un recuadro. */
                            style={{ mixBlendMode: 'multiply' }}
                        />
                        {/* Antigüedad de la marca, mismo lenguaje de sello que el resto
                            del sitio (LaurelSeal) — certificación/mérito, no correo. */}
                        {/* Al mismo tamaño que el sello del proveedor (44px): a 56px
                            pesaba más que el propio logotipo de Carpepel, que al ir
                            en h-8 es la marca más pequeña de la fila. */}
                        <LaurelSeal className="h-11 w-11 flex-shrink-0">
                            <text textAnchor="middle" fontSize="9" letterSpacing="1.5" fill="var(--kraft-deep)" className="font-mono" y="-5">
                                SINCE
                            </text>
                            <text textAnchor="middle" fontSize="20" fontWeight="700" fill="var(--kraft-deep)" className="font-heading" y="14">
                                2017
                            </text>
                        </LaurelSeal>
                        {/* Mismas cuentas que llevan al FAQPage/sameAs del schema —
                            la señal de autoridad solo cuenta si el enlace también
                            es visible en la página, no solo en el JSON-LD. */}
                        <div className="flex items-center gap-3" data-testid="footer-social">
                            {SOCIAL.map(({ name, href, Icon }) => (
                                <a
                                    key={name}
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`Carpepel en ${name}`}
                                    className="transition-colors duration-200"
                                    style={{ color: 'var(--ink-soft)' }}
                                    onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--kraft-deep)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ink-soft)'; }}
                                >
                                    <Icon className="w-[18px] h-[18px]" />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Sello de proveedor: discreto, en el centro para no chocar con el
                        botón flotante de WhatsApp (fijo en la esquina inferior derecha). */}
                    <div className="flex items-center gap-2.5" data-testid="footer-supplier-seal">
                        {/* El archivo de Supabase pesaba 955 KB y medía
                            1374×1556 px para dibujarse a 44×44: casi un mega
                            por cada visita a CUALQUIER página, porque el
                            footer va en todas. Lighthouse móvil lo señalaba
                            tres veces a la vez (uses-responsive-images,
                            offscreen-images, modern-image-formats) y era la
                            imagen más pesada del sitio con diferencia.
                            Ahora se sirve a 88 px —el doble del tamaño de
                            pantalla, para que se vea nítido en retina— desde
                            public/, que además es el propio origen: sin DNS
                            ni TLS extra hacia Supabase solo para el sello.
                            Pasó de 955 KB a 10 KB.
                            `lazy` porque está al final de la página y casi
                            nadie llega ahí, y width/height para que reserve
                            su hueco y no empuje la fila al aparecer. */}
                        <img
                            src="/austin_star_paper_seal_88.png"
                            alt="Austin Star Paper"
                            width="44"
                            height="44"
                            loading="lazy"
                            decoding="async"
                            className="h-11 w-11 object-contain opacity-90"
                        />
                        <div className="leading-tight">
                            <p className="text-[9px] uppercase tracking-widest text-[var(--ink-soft)] font-bold">Proveedor Oficial</p>
                            <p className="text-xs font-medium text-[var(--ink-soft)]">Austin Star Paper</p>
                        </div>
                    </div>

                    <div className="flex flex-col items-center md:items-end gap-1.5 md:pr-16">
                        {/* La página de rastreo existía sin ruta ni enlace: nadie podía
                            llegar a ella. Este es el único acceso visible del sitio. */}
                        <div className="flex items-center gap-3">
                            <Link
                                to="/rastreo"
                                data-testid="footer-tracking-link"
                                className="font-mono text-[11px] uppercase tracking-[0.14em] transition-opacity hover:opacity-70"
                                style={{ color: 'var(--kraft-deep)' }}
                            >
                                Rastrear pedido
                            </Link>
                            <span aria-hidden="true" className="text-[var(--ink-dim)]">·</span>
                            <Link
                                to="/privacidad"
                                data-testid="footer-privacy-link"
                                className="font-mono text-[11px] uppercase tracking-[0.14em] transition-opacity hover:opacity-70 text-[var(--ink-soft)]"
                            >
                                Aviso de Privacidad
                            </Link>
                            <span aria-hidden="true" className="text-[var(--ink-dim)]">·</span>
                            <Link
                                to="/terminos"
                                data-testid="footer-terms-link"
                                className="font-mono text-[11px] uppercase tracking-[0.14em] transition-opacity hover:opacity-70 text-[var(--ink-soft)]"
                            >
                                Términos y Condiciones
                            </Link>
                        </div>
                        <p className="text-xs text-[var(--ink-soft)]">Distribución de insumos alimenticios y papel grado alimenticio</p>
                    </div>
                </div>
            </div>
        </footer>
    );
}
